// OwnerLayout.js
import React, { useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import OwnerSidebar from './OwnerSidebar';
import { OwnerContext } from '../../context/OwnerContext';
import TopNavBar from '../commons/TopNavBar';
import MobileOwnerSidebar from './MobileOwnerSidebar';
import OwnerOnboarding from '../onboarding/OwnerOnboarding';
import '../../styles/TrainerLayout.css';

/**
 * OwnerLayout - Main layout wrapper for owner/pet owner pages
 *
 * Architecture:
 * ┌─────────────────────────────────────────────────────────────┐
 * │                    layout-container                          │
 * │  ┌──────────┐  ┌──────────────────────────────────────────┐ │
 * │  │          │  │              main-area                    │ │
 * │  │ sidebar  │  │  ┌────────────────────────────────────┐  │ │
 * │  │ (fixed)  │  │  │         TopNavBar                  │  │ │
 * │  │          │  │  ├────────────────────────────────────┤  │ │
 * │  │          │  │  │         page-content               │  │ │
 * │  │          │  │  │    ┌────────────────────────┐      │  │ │
 * │  │          │  │  │    │      {children}        │      │  │ │
 * │  │          │  │  │    └────────────────────────┘      │  │ │
 * │  └──────────┘  └──────────────────────────────────────────┘ │
 * └─────────────────────────────────────────────────────────────┘
 *
 * Props:
 * @param {React.ReactNode} children - Page content to render
 * @param {boolean} noPadding - If true, removes default padding from page-content
 * @param {boolean} fullWidth - If true, removes max-width constraint (for calendars, tables, etc.)
 * @param {string} className - Additional class name for page-content
 */
const OwnerLayout = ({
                         children,
                         noPadding = false,
                         fullWidth = true,
                         className = ''
                     }) => {
    const { t } = useTranslation('common');
    const contextValue = useContext(OwnerContext);
    const location = useLocation();

    // Safely extract owner from context
    const owner = contextValue?.owner || null;

    // Initialize state from localStorage or default to false
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
        const saved = localStorage.getItem('ownerSidebarCollapsed');
        return saved !== null ? JSON.parse(saved) : false;
    });

    // Save state to localStorage whenever it changes
    useEffect(() => {
        localStorage.setItem('ownerSidebarCollapsed', JSON.stringify(isSidebarCollapsed));
    }, [isSidebarCollapsed]);

    const toggleSidebarCollapse = () => {
        setIsSidebarCollapsed(!isSidebarCollapsed);
    };

    // Extract userId - accept both string and number IDs
    const userId = owner?.id ?? null;

    // Build page-content class names
    const pageContentClasses = [
        'page-content',
        noPadding && 'page-content--no-padding',
        fullWidth && 'page-content--full-width',
        className
    ].filter(Boolean).join(' ');

    return (
        <div className={`layout-container ${isSidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
            {/* Desktop sidebar (hidden on mobile via CSS) */}
            <aside className="sidebar">
                <OwnerSidebar
                    isCollapsed={isSidebarCollapsed}
                    onToggleCollapse={toggleSidebarCollapse}
                    activeRoute={location.pathname}
                />
            </aside>

            {/* Mobile sidebar with overlay */}
            <div className="mobile-sidebar-container">
                <MobileOwnerSidebar />
            </div>

            {/* Main content area - handles all positioning relative to sidebar */}
            <main className="main-area">
                <TopNavBar pageTitle={t('dashboard')} userId={userId} userType="OWNER" />

                {/* Page content wrapper - children style relative to THIS container */}
                <div className={pageContentClasses}>
                    {children}
                </div>
            </main>

            {/* Owner Onboarding Walkthrough */}
            <OwnerOnboarding />
        </div>
    );
};

export default OwnerLayout;